import { createReducer, on } from '@ngrx/store';
import { SelectedSubproject, Subproject, UserSession } from '@shared/models';
import { UserDetailsActions } from './actions';

export const UserDetailsFeatureName = 'userDetails';

export interface UserDetailsState {
  data: UserSession;
}

export const initialState: UserDetailsState = {
  data: null,
};

export const userDetailsReducer = createReducer(
  initialState,
  on(UserDetailsActions.setUserSession, (state, { userDetails }) => ({
    ...state,
    data: userDetails,
  })),
  on(UserDetailsActions.updateUserSession, (state, { userDetails }) => {
    const newSelectedSubproject: SelectedSubproject = getSelectedSubproject(
      state.data.selectedSubproject,
      userDetails.subprojects
    );

    const newProjectSetup = newSelectedSubproject
      ? state.data.selectedSubproject?.data
      : null;
    if (
      newSelectedSubproject &&
      newSelectedSubproject?.id ===
        newProjectSetup?.subprojectConfiguration?.subprojectId
    ) {
      newSelectedSubproject.data = newProjectSetup;
    }

    return {
      ...state,
      data: {
        ...state.data,
        modules: userDetails.modules,
        clientsList: userDetails.clientsList,
        subprojects: userDetails.subprojects,
        selectedSubproject: newSelectedSubproject,
        selectedSubprojectId: newSelectedSubproject?.id,
        userPreferences: userDetails.userPreferences,
        projects: userDetails.projects,
        settingsRight: userDetails.settingsRight,
        userAdminRight: userDetails.userAdminRight,
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email: userDetails.email,
        clientSupport: userDetails.clientSupport,
        accountType: userDetails.accountType,
        colorTypes: userDetails.colorTypes,
      },
    };
  }),
  on(UserDetailsActions.setUserPreferences, (state, { userPreferences }) => ({
    ...state,
    data: {
      ...state.data,
      userPreferences,
    },
  })),
  on(
    UserDetailsActions.setTermsConditionsAccepted,
    (state, { termsConditionsAccepted }) => ({
      ...state,
      data: {
        ...state.data,
        termsConditionsAccepted,
      },
    })
  ),
  on(
    UserDetailsActions.setSelectedSubproject,
    (state, { selectedSubproject }) => ({
      ...state,
      data: {
        ...state.data,
        selectedSubproject,
        selectedSubprojectId: selectedSubproject?.id,
        subprojects: state.data.subprojects.map((x) =>
          x.id !== selectedSubproject?.id
            ? x
            : {
                ...x,
                fileUpload:
                  selectedSubproject?.data?.subprojectConfiguration
                    ?.fileUpload ?? x.fileUpload,
              }
        ),
      },
    })
  ),
  on(UserDetailsActions.setProjectSetup, (state, { projectSetup }) => ({
    ...state,
    data: {
      ...state.data,
      selectedSubproject: {
        ...state.data.selectedSubproject,
        data: projectSetup,
      },
      subprojects: state.data.subprojects.map((x) =>
        x.id !== state.data.selectedSubprojectId
          ? x
          : {
              ...x,
              fileUpload:
                projectSetup?.subprojectConfiguration?.fileUpload ??
                x.fileUpload,
            }
      ),
    },
  })),
  on(UserDetailsActions.setSelectedClientId, (state, { clientId }) => ({
    ...state,
    data: {
      ...state.data,
      selectedClientId: clientId,
      selectedSubproject: null,
      selectedSubprojectId: null,
    },
  })),
  on(UserDetailsActions.setEnableDigitalSignature, (state, { updateData }) => ({
    ...state,
    data: {
      ...state.data,
      clientsList: state.data.clientsList.map((x) => {
        if (!updateData) {
          return x;
        }

        const found = updateData.find((d) => d.id === x.id);
        if (!found) {
          return x;
        }

        return { ...x, enableDigitalSignature: found.enableDigitalSignature };
      }),
    },
  })),
  on(UserDetailsActions.setClientSupport, (state, { clientSupport }) => ({
    ...state,
    data: {
      ...state.data,
      clientSupport,
    },
  })),
  on(UserDetailsActions.setSubprojects, (state, { subprojects }) => ({
    ...state,
    data: {
      ...state.data,
      subprojects,
    },
  })),

  //#region  Project Setup

  on(UserDetailsActions.setSubprojectReleased, (state, { subprojectId }) => ({
    ...state,
    data: {
      ...state.data,
      selectedSubproject: {
        ...state.data.selectedSubproject,
        isReleased: true,
        data: {
          ...state.data.selectedSubproject.data,
          isReleased: true,
        },
      },
      subprojects: state.data.subprojects.map((x) =>
        x.id === subprojectId
          ? {
              ...x,
              isReleased: true,
            }
          : x
      ),
    },
  })),
  on(
    UserDetailsActions.setSubprojectConfiguration,
    (state, { subprojectConfiguration }) => ({
      ...state,
      ...setProjectSetupItem(
        state,
        'subprojectConfiguration',
        subprojectConfiguration
      ),
    })
  ),
  on(UserDetailsActions.setStatusTrackers, (state, { statusTrackers }) => ({
    ...state,
    ...setProjectSetupItem(state, 'statusTrackers', statusTrackers),
  })),
  on(
    UserDetailsActions.setLocationCategories,
    (state, { locationCategoryList }) => ({
      ...state,
      ...setProjectSetupItem(
        state,
        'locationCategoryList',
        locationCategoryList
      ),
    })
  ),
  on(UserDetailsActions.setLocations, (state, { locationList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'locationList', locationList),
  })),
  on(UserDetailsActions.setCustomData, (state, { customDataList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'customDataList', customDataList),
  })),
  on(
    UserDetailsActions.setCustomDataValues,
    (state, { customDataValueList }) => ({
      ...state,
      ...setProjectSetupItem(state, 'customDataValueList', customDataValueList),
    })
  ),
  on(UserDetailsActions.setActivityGroups, (state, { activityGroupList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'activityGroupList', activityGroupList),
  })),
  on(UserDetailsActions.setActivities, (state, { activityList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'activityList', activityList),
  })),
  on(
    UserDetailsActions.setActivityMobilisations,
    (state, { activityMobilisationList }) => ({
      ...state,
      ...setProjectSetupItem(
        state,
        'activityMobilisationList',
        activityMobilisationList
      ),
    })
  ),
  on(UserDetailsActions.setActivityStreams, (state, { activityStreams }) => ({
    ...state,
    ...setProjectSetupItem(state, 'activityStreams', activityStreams),
  })),
  on(
    UserDetailsActions.setActivityStreamInlineCustomData,
    (state, { activityStreamInlineCustomDataList }) => ({
      ...state,
      ...setProjectSetupItem(
        state,
        'activityStreamInlineCustomDataList',
        activityStreamInlineCustomDataList
      ),
    })
  ),
  on(UserDetailsActions.setWeather, (state, { weatherList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'weatherList', weatherList),
  })),
  on(
    UserDetailsActions.setWeatherValues,
    (state, { weatherDataTypeValueList }) => ({
      ...state,
      ...setProjectSetupItem(
        state,
        'weatherDataTypeValueList',
        weatherDataTypeValueList
      ),
    })
  ),
  on(UserDetailsActions.setHse, (state, { hseList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'hseList', hseList),
  })),
  on(UserDetailsActions.setPersonnel, (state, { personnelList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'personnelList', personnelList),
  })),
  on(UserDetailsActions.setConsumables, (state, { consumableList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'consumableList', consumableList),
  })),
  on(UserDetailsActions.setApprovers, (state, { approverList }) => ({
    ...state,
    ...setProjectSetupItem(state, 'approverList', approverList),
  })),
  on(
    UserDetailsActions.setForecastCategories,
    (state, { forecastCategories }) => ({
      ...state,
      ...setProjectSetupItem(state, 'forecastCategories', forecastCategories),
    })
  ),
  on(UserDetailsActions.setFreeTextFields, (state, { freeTextFields }) => ({
    ...state,
    ...setProjectSetupItem(state, 'freeTextFields', freeTextFields),
  })),
  on(UserDetailsActions.setProgress, (state, { progress }) => ({
    ...state,
    ...setProjectSetupItem(state, 'progress', progress),
  })),
  on(
    UserDetailsActions.setProgressWithoutTarget,
    (state, { progressWithoutTarget }) => ({
      ...state,
      ...setProjectSetupItem(
        state,
        'progressWithoutTarget',
        progressWithoutTarget
      ),
    })
  ),
  on(UserDetailsActions.setTableOfColumns, (state, { tableOfColumns }) => ({
    ...state,
    ...setProjectSetupItem(state, 'tableOfColumns', tableOfColumns),
  })),
  on(UserDetailsActions.setHiddenSubtabs, (state, { hiddenSubtabs }) => ({
    ...state,
    ...setProjectSetupItem(state, 'hiddenSubtabs', hiddenSubtabs),
  })),
  on(
    UserDetailsActions.setReportConfiguration,
    (state, { reportConfiguration }) => ({
      ...state,
      ...setProjectSetupItem(state, 'reportConfiguration', reportConfiguration),
    })
  ),
  on(UserDetailsActions.setNamingConvention, (state, { namingConvention }) => ({
    ...state,
    ...setProjectSetupItem(state, 'namingConvention', namingConvention),
  }))

  //#endregion
);

function getSelectedSubproject(
  selectedSubproject: SelectedSubproject,
  subprojects: Subproject[]
): SelectedSubproject {
  let newSelectedSubprojectDetails = subprojects?.find(
    (x) => x.id === selectedSubproject?.id
  );

  if (!newSelectedSubprojectDetails && subprojects?.length > 0) {
    newSelectedSubprojectDetails = subprojects[0];
  }

  return subprojects?.length && newSelectedSubprojectDetails
    ? Object.assign({}, newSelectedSubprojectDetails)
    : null;
}

function setProjectSetupItem(
  state: UserDetailsState,
  property: string,
  data: any
): object {
  return {
    data: {
      ...state.data,
      selectedSubproject: {
        ...state.data.selectedSubproject,
        data: {
          ...state.data.selectedSubproject.data,
          [property]: data,
        },
      },
    },
  };
}
